import FormPublicWrapper from "@/components/form-public-wrapper";
import { InputIcon } from "@/components/input-icon";
import {
  PublicAuthCard,
  PublicAuthFooterLink,
  PublicAuthSubmitButton,
  publicAuthInputClassName,
  publicAuthLabelClassName,
} from "@/components/public-auth-card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import Routes from "@/routes/paths";
import { UserService } from "@/services/api/user";
import { cn } from "@/utils";
import { getToastErrorContent } from "@/utils/functions/api-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { EyeIcon, EyeOffIcon, Lock } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";

const FormSchema = z
  .object({
    password: z.string().min(1, { message: "Campo obrigatório" }),
    confirmPassword: z.string().min(1, { message: "Campo obrigatório" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "As senhas não correspondem",
  });

type FormSchemaType = z.infer<typeof FormSchema>;

type ScreenProps = {
  token?: string;
};

export default function UpdatePasswordScreen({ token }: ScreenProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [passwordType, setPasswordType] = useState("password");
  const [passwordConfirmType, setPasswordConfirmType] = useState("password");

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  async function onSubmit(data: FormSchemaType) {
    try {
      if (!token) {
        return;
      }

      const { password } = FormSchema.parse(data);

      await UserService.updatePassword({ token, password });

      toast({
        variant: "success",
        description: "Sua senha foi atualizada com sucesso.",
      });

      navigate(Routes.base, { replace: true });
    } catch (error) {
      toast(getToastErrorContent(error));
    }
  }

  const { isSubmitting } = form.formState;

  const InputPasswordIcon = passwordType === "password" ? EyeOffIcon : EyeIcon;

  const InputPasswordConfirmIcon =
    passwordConfirmType === "password" ? EyeOffIcon : EyeIcon;

  return (
    <FormPublicWrapper variant="modern">
      <PublicAuthCard
        title="Nova senha"
        description="Defina uma nova senha para acessar sua conta"
        footer={
          <PublicAuthFooterLink
            text="Lembrou sua senha?"
            linkText="Voltar ao login"
            to={Routes.base}
          />
        }
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-5"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={publicAuthLabelClassName}>
                    Senha
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <InputIcon
                        icon={
                          <InputPasswordIcon
                            size={18}
                            className="text-slate-400 transition-colors hover:text-slate-200"
                          />
                        }
                        onClick={() =>
                          setPasswordType((prev) =>
                            prev === "password" ? "text" : "password"
                          )
                        }
                      >
                        <Input
                          {...field}
                          type={passwordType}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          className={cn(publicAuthInputClassName, "pr-10")}
                        />
                      </InputIcon>
                    </div>
                  </FormControl>

                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={publicAuthLabelClassName}>
                    Confirmar senha
                  </FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <InputIcon
                        icon={
                          <InputPasswordConfirmIcon
                            size={18}
                            className="text-slate-400 transition-colors hover:text-slate-200"
                          />
                        }
                        onClick={() =>
                          setPasswordConfirmType((prev) =>
                            prev === "password" ? "text" : "password"
                          )
                        }
                      >
                        <Input
                          {...field}
                          type={passwordConfirmType}
                          placeholder="••••••••"
                          autoComplete="new-password"
                          className={cn(publicAuthInputClassName, "pr-10")}
                        />
                      </InputIcon>
                    </div>
                  </FormControl>

                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />

            <PublicAuthSubmitButton
              isSubmitting={isSubmitting}
              loadingText="Atualizando..."
            >
              Atualizar senha
            </PublicAuthSubmitButton>
          </form>
        </Form>
      </PublicAuthCard>
    </FormPublicWrapper>
  );
}
